// "Pages you open most" on the home screen.
//
// Counted in THIS browser only, per signed-in person: nothing is sent to the server and
// nothing is shared between people or devices. Every read and write is guarded, because
// storage can be blocked or full, and the home screen must render the same without it.
const KEY_PREFIX = 'cp.pageVisits.v1:';
// More than the addresses that exist today (some 66 pages and dashboards), so in
// practice nothing is ever dropped.
const MAX_PATHS = 200;

type VisitCounts = Record<string, number>;

function read(userKey: string): VisitCounts {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + userKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: VisitCounts = {};
    for (const [path, count] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof count === 'number' && Number.isFinite(count) && count > 0) out[path] = count;
    }
    return out;
  } catch {
    return {};
  }
}

/** Count one opening of a page. `path` is the page's address without its query string. */
export function recordPageVisit(userKey: string | null | undefined, path: string): void {
  if (!userKey || !path) return;
  try {
    const counts = read(userKey);
    counts[path] = (counts[path] ?? 0) + 1;
    // Keep the list small: drop the least-opened addresses beyond the cap. Never the one
    // just counted: it starts at 1, so a new page would be dropped on every opening and
    // could never be learnt.
    const kept = Object.entries(counts)
      .sort((a, b) => Number(b[0] === path) - Number(a[0] === path) || b[1] - a[1])
      .slice(0, MAX_PATHS);
    window.localStorage.setItem(KEY_PREFIX + userKey, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // Storage unavailable: the shortcut row simply stays empty.
  }
}

/** The addresses this person has opened most in this browser, most-opened first. */
export function topVisitedPaths(userKey: string | null | undefined, limit: number): string[] {
  if (!userKey) return [];
  return Object.entries(read(userKey))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, Math.max(0, limit))
    .map(([path]) => path);
}
